import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { posBackendApi } from '@/lib/api-client';

export type ProgramType = 'points_per_product' | 'points_per_dollar' | 'points_per_visit';

export interface RewardProgram {
  id: string;
  tenantId: string;
  name: string;
  programType: ProgramType;
  pointsPerDollar: string;
  pointsPerVisit: number;
  minOrderAmount: string;
  redemptionRate: string;
  cooldownMinutes: number;
  locationScope: 'all' | 'specific';
  isActive: boolean;
  validFrom: string | null;
  validTo: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRewardProgramInput {
  name: string;
  programType: ProgramType;
  pointsPerDollar?: number;
  pointsPerVisit?: number;
  minOrderAmount?: number;
  redemptionRate?: number;
  cooldownMinutes?: number;
  locationScope?: 'all' | 'specific';
  validFrom?: string;
  validTo?: string;
}

const KEY = ['customer-engagement', 'reward-programs'] as const;

export function useRewardPrograms(active?: boolean) {
  return useQuery({
    queryKey: [...KEY, { active }],
    queryFn: () =>
      posBackendApi.get<{ data: RewardProgram[]; meta: { count: number } }>('/customer-engagement/reward-programs', {
        active: active === undefined ? undefined : String(active),
      }),
    staleTime: 30_000,
  });
}

export function useCreateRewardProgram() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRewardProgramInput) =>
      posBackendApi.post<{ data: RewardProgram }>('/customer-engagement/reward-programs', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeactivateRewardProgram() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      posBackendApi.delete<{ data: { id: string; isActive: false } }>(`/customer-engagement/reward-programs/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
